<template>
    <el-card class="salesforce-contact-settings-card mb-3 mt-3">
        <!-- Start loader -->
        <div class="text-center mt-2"
             v-if="isLoading">
            <h5>
                <i class="fa fa-spinner fa-spin"></i>
                <span>Loading...</span>
            </h5>
        </div>
        <!-- End loader -->
        <div v-else>
            <div class="mb-3">
                <el-row>
                    <span>Control whether contact changes are sent to Salesforce, received from Salesforce, or both by selecting the appropriate direction.</span>
                </el-row>
                <el-row class="mt-1">
                    <span>
                      <b>Best Practices</b>: Both Salesforce and {{ statics }} <b>can update</b> contacts and/or leads information (<i
                        class="fas fa-arrows-alt-h"></i>), but a good practice used, {{ statics }} <b>cannot create</b> new contacts/leads (<i class="fas fa-arrow-left"></i>) in Salesforce.
                    </span>
                </el-row>
            </div>

            <!-- Start Contact Configuration -->
            <div class="d-flex">
                <h5>Contact</h5>
                <el-tooltip
                        class="item"
                        effect="dark"
                        placement="top">
                    <div slot="content">
                        <p v-if="!hasFeatureVersion2 && !model.settings.contact_settings.enable_lead">Enable Contact as default Salesforce
                            CRM Module</p>
                        <p v-else-if="!hasFeatureVersion2">Contacts can't be enabled if Lead is already enabled</p>
                        <p v-else-if="hasFeatureVersion2 && !model.settings.contact_settings.enable_lead">At least one module (Contact or Lead) must remain enabled.</p>
                        <p v-else-if="model.settings.contact_settings.enable_contact">Disable Contact</p>
                        <p v-else>Enable Contact</p>
                    </div>
                    <el-switch
                            v-model="model.settings.contact_settings.enable_contact"
                            :disabled="(!hasFeatureVersion2 && model.settings.contact_settings.enable_lead) || (hasFeatureVersion2 && !model.settings.contact_settings.enable_lead && model.settings.contact_settings.enable_contact)"
                            active-color="#00a344"
                            class="mt-1 ml-2"
                    />
                </el-tooltip>
            </div>

            <div class="row form-group mt-3">
                <!-- Start Aloware and Salesforce Labels -->
                <div class="mb-1" :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <small class="font-weight-bold">Aloware</small>
                </div>
                <div class="col-md-2 mb-1 text-center">
                    <small class="font-weight-bold">Direction</small>
                </div>
                <div class="mb-1" :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <small class="font-weight-bold">Salesforce</small>
                </div>
            </div>
            <!-- End Aloware and Salesforce Labels -->

            <!-- Start Aloware Fields -->
            <div class="row form-group mt-3">
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            readonly
                            :disabled="!model.settings.contact_settings.enable_contact || !model.settings.contact_settings.contacts.should_create"
                            :value="getSalesforceLabels('contact.create')"
                    />
                </div>
                <!-- End Aloware Fields -->

                <div class="col-md-2 text-center">
                    <direction-selector :initial-state="model.settings.contact_settings.contacts.create_direction"
                                        :disabled="!model.settings.contact_settings.enable_contact
                                || !model.settings.contact_settings.contacts.should_create"
                                        :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                                        @change="direction => model.settings.contact_settings.contacts.create_direction = direction">
                    </direction-selector>
                </div>

                <!-- Start Salesforce Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            readonly
                            :disabled="!model.settings.contact_settings.enable_contact || !model.settings.contact_settings.contacts.should_create"
                            :value="getSalesforceLabels('contact.create')"
                    />
                </div>
                <!-- End Salesforce Fields -->

                <!-- Start Delete button -->
                <div class="col-md-2 text-center"
                    v-if="!hasFeatureVersion2">
                    <el-tooltip
                            class="item"
                            effect="dark"
                            placement="top">
                        <div slot="content">
                            <p>Enable creating a new contact in Salesforce when a new contact is created in
                                Aloware</p>
                        </div>
                        <el-switch
                                v-model="model.settings.contact_settings.contacts.should_create"
                                :disabled="!model.settings.contact_settings.enable_contact"
                                class="mt-1 ml-2"
                                active-color="#00a344"
                        />
                    </el-tooltip>
                </div>
                <!-- End Delete Button -->
            </div>
            <!-- End Contact Configuration -->

            <!-- Start Aloware Fields -->
            <div class="row form-group mt-3">
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            readonly
                            :disabled="!model.settings.contact_settings.enable_contact || !model.settings.contact_settings.contacts.should_update"
                            :value="getSalesforceLabels('contact.update')"
                    />
                </div>
                <!-- End Aloware Fields -->

                <div class="col-md-2 text-center">
                    <direction-selector :initial-state="model.settings.contact_settings.contacts.update_direction"
                                        :disabled="!isDirectionEditable(getSalesforceLabels('contact.update'))
                                  || !model.settings.contact_settings.enable_contact
                                  || !model.settings.contact_settings.contacts.should_update"
                                        :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                                        @change="direction => model.settings.contact_settings.contacts.update_direction = direction">
                    </direction-selector>
                </div>

                <!-- Start Salesforce Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            readonly
                            :disabled="!model.settings.contact_settings.enable_contact || !model.settings.contact_settings.contacts.should_update"
                            :value="getSalesforceLabels('contact.update')"
                    />
                </div>
                <!-- End Salesforce Fields -->

                <!-- Start Enable button -->
                <div class="col-md-2 text-center"
                    v-if="!hasFeatureVersion2">
                    <el-tooltip
                            class="item"
                            effect="dark"
                            placement="top">
                        <div slot="content">
                            <p>Enable updating a contact in Salesforce when a contact is updated in Aloware</p>
                        </div>
                        <el-switch
                                v-model="model.settings.contact_settings.contacts.should_update"
                                :disabled="!model.settings.contact_settings.enable_contact"
                                class="mt-1 ml-2"
                                active-color="#00a344"
                        />
                    </el-tooltip>
                </div>
                <!-- End Enable Button -->
            </div>
            <!-- End Contact Configuration -->

            <!-- Start Lead Configuration -->
            <div class="d-flex">
                <h5>Lead</h5>
                <el-tooltip
                        class="item"
                        effect="dark"
                        placement="right">
                    <div slot="content">
                        <p v-if="!hasFeatureVersion2 && !model.settings.contact_settings.enable_contact">Enable Lead as
                            default Salesforce
                            CRM Module</p>
                        <p v-else-if="!hasFeatureVersion2">Leads can't be enabled if Contact is already enabled</p>
                        <p v-else-if="hasFeatureVersion2 && !model.settings.contact_settings.enable_contact">At least one module (Contact or Lead) must remain enabled.</p>
                        <p v-else-if="model.settings.contact_settings.enable_lead">Disable Lead</p>
                        <p v-else>Enable Lead</p>
                    </div>
                    <el-switch
                            v-model="model.settings.contact_settings.enable_lead"
                            :disabled="(!hasFeatureVersion2 && model.settings.contact_settings.enable_contact) || (hasFeatureVersion2 && !model.settings.contact_settings.enable_contact && model.settings.contact_settings.enable_lead)"
                            class="mt-1 ml-2"
                            active-color="#00a344"
                    />
                </el-tooltip>
            </div>

            <div class="row form-group mt-3">
                <!-- Start Aloware and Salesforce Labels -->
                <div class="mb-1" :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <small class="font-weight-bold">Aloware</small>
                </div>
                <div class="col-md-2 mb-1 text-center">
                    <small class="font-weight-bold">Direction</small>
                </div>
                <div class="mb-1" :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <small class="font-weight-bold">Salesforce</small>
                </div>
            </div>
            <!-- End Aloware and Salesforce Labels -->

            <div class="row form-group mt-3">
                <!-- Start Aloware Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('contact.create')"
                            :disabled="!model.settings.contact_settings.enable_lead || !model.settings.contact_settings.leads.should_create"
                            readonly
                    />
                </div>
                <!-- End Aloware Fields -->

                <div class="col-md-2 text-center">
                    <direction-selector :initial-state="model.settings.contact_settings.leads.create_direction"
                                        :disabled="!model.settings.contact_settings.enable_lead
                                || !model.settings.contact_settings.leads.should_create"
                                        :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                                        @change="direction => model.settings.contact_settings.leads.create_direction = direction">
                    </direction-selector>
                </div>

                <!-- Start Salesforce Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('lead.create')"
                            :disabled="!model.settings.contact_settings.enable_lead || !model.settings.contact_settings.leads.should_create"
                            readonly
                    >
                    </el-input>
                </div>
                <!-- End Salesforce Fields -->

                <!-- Start Delete button -->
                <div class="col-md-2 text-center"
                     v-if="!hasFeatureVersion2">
                    <el-tooltip
                            class="item"
                            effect="dark"
                            placement="right">
                        <div slot="content">
                            <p> Enable creating a new lead in Salesforce when a new contact is created in
                                Aloware </p>
                        </div>
                        <el-switch
                                v-model="model.settings.contact_settings.leads.should_create"
                                :disabled="!model.settings.contact_settings.enable_lead"
                                class="mt-1 ml-2"
                                active-color="#00a344"
                        />
                    </el-tooltip>
                </div>
                <!-- End Delete Button -->
            </div>

            <div class="row form-group mt-3">
                <!-- Start Aloware Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('contact.update')"
                            :disabled="!model.settings.contact_settings.enable_lead || !model.settings.contact_settings.leads.should_update"
                            readonly
                    />
                </div>
                <!-- End Aloware Fields -->

                <div class="col-md-2 text-center">
                    <direction-selector :initial-state="model.settings.contact_settings.leads.update_direction"
                                        :disabled="!isDirectionEditable(getSalesforceLabels('lead.update'))
                                  || !model.settings.contact_settings.enable_lead
                                  || !model.settings.contact_settings.leads.should_update"
                                        :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                                        @change="direction => model.settings.contact_settings.leads.update_direction = direction">
                    </direction-selector>
                </div>

                <!-- Start Salesforce Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('lead.update')"
                            :disabled="!model.settings.contact_settings.enable_lead || !model.settings.contact_settings.leads.should_update"
                            readonly
                    >
                    </el-input>
                </div>
                <!-- End Salesforce Fields -->

                <!-- Start Delete button -->
                <div class="col-md-2 text-center"
                    v-if="!hasFeatureVersion2">
                    <el-tooltip
                            class="item"
                            effect="dark"
                            placement="right">
                        <div slot="content">
                            <p> Enable updating a lead in Salesforce when a contact is updated in Aloware </p>
                        </div>
                        <el-switch
                                v-model="model.settings.contact_settings.leads.should_update"
                                :disabled="!model.settings.contact_settings.enable_lead"
                                class="mt-1 ml-2"
                                active-color="#00a344"
                        />
                    </el-tooltip>
                </div>
                <!-- End Delete Button -->
            </div>
            <!-- End Lead Configuration -->
            <!-- Start Prioritization Section -->
            <template
                    v-if="hasFeatureVersion2 && model.settings.contact_settings.enable_contact && model.settings.contact_settings.enable_lead">
                <el-alert class="mt-2"
                          type="warning"
                          :closable="false">
                    <div class="sync-priority-content text-black">
                        <p class="sync-priority-description">
                            <b class="section-subheader">Sync Leads and Contacts with Phone Number
                                Prioritization</b><br/>
                            Simultaneously syncing leads and contacts is possible. However, in cases where a lead or
                            contact shares the same phone number, you need to set a priority to determine which record
                            will be synced as the primary in Aloware. What will be your chosen priority?
                        </p>
                        <el-select
                                v-model="model.settings.contact_settings.priority"
                                placeholder="Select"
                                class="sync-priority-select">
                            <el-option label="Lead" :value="MODULE_LEAD"></el-option>
                            <el-option label="Contact" :value="MODULE_CONTACT"></el-option>
                        </el-select>
                    </div>
                </el-alert>
            </template>
            <!-- End Prioritization Section -->
        </div>
    </el-card>
</template>

<script>
import DirectionSelector from "../direction-selector.vue"
import * as IntegrationPropertyMapDirections from '../../../constants/integration-property-map-directions'
import * as SalesforceContactSettingsLabels from "../../../constants/integration-salesforce-contact-settings-labels"
import {mapActions} from 'vuex'
import {MODULE_LEAD, MODULE_CONTACT} from "../../../../constants/integration_modules";
import {SALESFORCE_INTEGRATION} from "../../../../constants/integrations";

export default {
    name: 'salesforce-contact-settings',
    components: {DirectionSelector},
    props: {
        model: {
            type: Object | null,
            required: true
        },
        statics: {
            type: String,
            required: true,
        }
    },
    data: () => ({
        // ui
        isLoading: false,
        IntegrationPropertyMapDirections,
        default_settings: {},
        MODULE_LEAD,
        MODULE_CONTACT,
    }),
    computed: {
        hasFeatureVersion2() {
            return this.model?.credentials?.feature_version >= 2
        }
    },
    async created() {
        this.isLoading = true
        this.resetSalesforceSettings()
        this.isLoading = false
    },
    methods: {
        ...mapActions(['setRefreshPageIsNeeded', 'setCRMModuleWasChanged']),
        resetSalesforceSettings() {
            if (!this.model.settings) {
                this.$set(this.model, 'settings', {});
            }
            if (!this.model.settings.contact_settings) {
                this.$set(this.model.settings, 'contact_settings', {
                    enable_contact: false,
                    enable_lead: true,
                    contacts: {
                        should_create: true,
                        create_direction: IntegrationPropertyMapDirections.DIRECTION_INBOUND,
                        should_update: true,
                        update_direction: IntegrationPropertyMapDirections.DIRECTION_BI,
                    },
                    leads: {
                        should_create: true,
                        create_direction: IntegrationPropertyMapDirections.DIRECTION_INBOUND,
                        should_update: true,
                        update_direction: IntegrationPropertyMapDirections.DIRECTION_BI,
                    }
                });
            }

            // this is new flag and can be not able in old payloads
            if (this.hasFeatureVersion2 && !this.model.settings.contact_settings.hasOwnProperty('priority')) {
                this.$set(this.model.settings.contact_settings, 'priority', MODULE_LEAD)
            }

            this.default_settings = _.clone(this.model?.settings?.contact_settings)
        },
        isDirectionEditable(key) {
            switch (key) {
                case SalesforceContactSettingsLabels.LABEL_CONTACT_CREATE:
                case SalesforceContactSettingsLabels.LABEL_LEAD_CREATE:
                    return false
                case SalesforceContactSettingsLabels.LABEL_CONTACT_UPDATE:
                case SalesforceContactSettingsLabels.LABEL_LEAD_UPDATE:
                    return true
                default:
                    return true
            }
        },
        getSalesforceLabels(module_operation) {
            switch (module_operation) {
                case 'contact.create':
                    return SalesforceContactSettingsLabels.LABEL_CONTACT_CREATE
                case 'contact.update':
                    return SalesforceContactSettingsLabels.LABEL_CONTACT_UPDATE
                case 'lead.create':
                    return SalesforceContactSettingsLabels.LABEL_LEAD_CREATE
                case 'lead.update':
                    return SalesforceContactSettingsLabels.LABEL_LEAD_UPDATE
                default:
                    return ''
            }
        }
    },
    watch: {
        'model.settings.contact_settings.enable_contact': function (data) {
            const flag = data !== this.default_settings.enable_contact || this.model.settings.contact_settings.enable_lead !== this.default_settings.enable_lead
            this.setCRMModuleWasChanged(flag)
            if (this.model.credentials?.feature_version >= 2) {
                this.setRefreshPageIsNeeded((!this.default_settings.enable_contact && data) || (!this.default_settings.enable_lead && this.model.settings.contact_settings.enable_lead))
            } else {
                this.setRefreshPageIsNeeded(flag)
            }
        },
        'model.settings.contact_settings.enable_lead': function (data) {
            const flag = this.model.settings.contact_settings.enable_contact !== this.default_settings.enable_contact || data !== this.default_settings.enable_lead
            this.setCRMModuleWasChanged(flag)
            if (this.model.credentials?.feature_version >= 2) {
                this.setRefreshPageIsNeeded((!this.default_settings.enable_lead && data) || (!this.default_settings.enable_contact && this.model.settings.contact_settings.enable_contact))
            } else {
                this.setRefreshPageIsNeeded(flag)
            }
        }
    }
}
</script>

<style scoped>

.sync-priority-content {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
}

.sync-priority-description {
    font-size: 14px;
    line-height: 20px;
    margin: 0;
    flex: 1;
}

.sync-priority-select {
    width: 200px;
    flex-shrink: 0;
}

/* Override element-ui select styles */
.sync-priority-select :deep(.el-input__inner) {
    height: 36px;
    line-height: 36px;
    border-color: #d1d5db;
}
</style>
